import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MINUTE_MS } from 'data/constants'
// eslint-disable-next-line -- Need AA address for arbitrary userId prop, not just current user
import { useAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useEnvironment } from 'hooks/useEnvironment'
import { Address, SpaceAddressFromSpaceId, useBaseReads, useSpaceId } from 'use-towns-client'
import { getAddress, isAddress } from 'viem'

import { fetchTipLeaderboardItems } from './useTipLeaderboard'

type Result = {
    rank: number | undefined
}

/**
 * Hook to get the tip rank of a specific user within a specific space.
 * Only returns ranks 1-3 since badges only show for top 3 tippers.
 * Uses a single optimized query to fetch only top 3 users.
 */
export const useUserRank = (userId: string | undefined): Result => {
    const { accountAbstractionConfig } = useEnvironment()
    const { subgraphEndpoint } = useBaseReads()
    const spaceId = useSpaceId()

    // 1. Get rootKeyAddress from userId (assuming userId is the root key address)
    const rootKeyAddress = useMemo(() => {
        if (!userId || !isAddress(userId)) {
            return undefined
        }
        try {
            return getAddress(userId) as Address // Normalize
        } catch (e) {
            console.error('Invalid userId format:', userId, e)
            return undefined
        }
    }, [userId])

    // 2. Get abstractAccountAddress from rootKeyAddress
    const { data: targetAbstractAccountAddress } = useAbstractAccountAddress({
        rootKeyAddress,
    })

    // 3. Get spaceAddress
    const spaceAddress = useMemo(() => {
        if (!spaceId) {
            return undefined
        }
        return SpaceAddressFromSpaceId(spaceId)
    }, [spaceId])

    // 4. Determine the address to query for
    const userAddressToQuery = useMemo(() => {
        return accountAbstractionConfig !== undefined
            ? targetAbstractAccountAddress // Use AA address if AA is enabled
            : rootKeyAddress // Otherwise, use the root key address (userId)
    }, [accountAbstractionConfig, targetAbstractAccountAddress, rootKeyAddress])

    // 5. Query for top 3 tippers using shared function
    const { data: top3Data } = useQuery({
        queryKey: ['top3Tippers', spaceAddress],
        queryFn: () => {
            if (!subgraphEndpoint || !spaceAddress) {
                return []
            }
            return fetchTipLeaderboardItems(spaceAddress, subgraphEndpoint, 3)
        },
        enabled: !!subgraphEndpoint && !!spaceAddress,
        staleTime: MINUTE_MS,
        refetchInterval: MINUTE_MS,
        refetchOnWindowFocus: false,
    })

    // 6. Find user's rank in top 3
    const rank = useMemo(() => {
        if (!top3Data || !userAddressToQuery) {
            return undefined
        }

        const userIndex = top3Data.findIndex(
            (item) => item.user.toLowerCase() === userAddressToQuery.toLowerCase(),
        )

        // Return rank 1, 2, 3 if in top 3, otherwise undefined
        return userIndex !== -1 ? userIndex + 1 : undefined
    }, [top3Data, userAddressToQuery])

    return {
        rank,
    }
}
